import { useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import core, { App, GQL, View } from '@gin-betting/core';
import { createStructuredSelector } from 'reselect';

import { useMemoSelector } from 'hooks';
import {
    BettingReadyIndicatorContext,
    IBettingReadyIndicatorContext,
} from 'modules/betting-ready-indicator';
import { useReservedState } from 'services/garbage-collector';
import { fetchFeaturedCustomList } from 'utils';
import { selectFeaturedTournamentsFetchStatus } from '../../selectors';

interface Output {
    config: GQL.FeaturedZoneCustomListProviderFragment | null | undefined;
    fetchStatus: App.FetchStatus;
    readyIndicator: IBettingReadyIndicatorContext;
    matchesIds: string[];
    isSmall: boolean;
    isMedium: boolean;
    isLoadingConfig: boolean;
}

const {
    entities: {
        normalizeEntity,
        actions: { storeNormalizedEntities },
    },
    viewport: {
        selectors: { makeViewportSelector },
    },
    utils: { isFetchFinished },
    poller: { getPollerManager },
    hooks: { useDeepMemo },
} = core;

const selectProps = createStructuredSelector({
    lessThanOrEqual: makeViewportSelector('lessThanOrEqual'),
    fetchStatus: selectFeaturedTournamentsFetchStatus,
});

const useFeaturedTournamentPage = (): Output => {
    const { slug } = useParams<{ slug: string }>();

    const dispatch = useDispatch();
    const readyIndicator = useContext(BettingReadyIndicatorContext);
    const [isLoadingConfig, setLoadingConfig] = useState<boolean>(false);

    const [config, setConfig] = useState<
        GQL.FeaturedZoneCustomListProviderFragment | null | undefined
    >(undefined);
    const [matchesIds, setMatchesIds] = useReservedState('matches', []);
    const memMatchesIds = useDeepMemo(matchesIds);

    const {
        lessThanOrEqual: { small: isSmall, medium: isMedium },
        fetchStatus,
    } = useMemoSelector(selectProps, { location, slug });

    const requestFeaturedCustomList = useCallback(
        (isMounted: boolean) => {
            fetchFeaturedCustomList(slug)
                .then(response => {
                    if (!response || !response.data) return;

                    const {
                        config: nextConfig,
                        sportEventsList: { matches },
                    } = response.data.featuredCustomList;
                    const ids = matches.map(match => match.id);

                    const normalizedData = normalizeEntity({
                        matches,
                    }) as View.Normalized;

                    dispatch(
                        storeNormalizedEntities(normalizedData, {
                            isTopMarkets: true,
                        })
                    );

                    if (isMounted) {
                        setConfig(nextConfig);
                        setMatchesIds(ids);
                        setLoadingConfig(false);
                    }
                })
                .catch(err => console.error(err));
        },
        [dispatch, setMatchesIds, slug]
    );

    useEffect(() => {
        let isMounted = true;

        setLoadingConfig(true);
        requestFeaturedCustomList(isMounted);

        const pollInterval$ = getPollerManager().getIntervalObserve();
        const getSportEventsSubscription = pollInterval$.subscribe(() => {
            requestFeaturedCustomList(isMounted);
        });

        return () => {
            isMounted = false;
            getSportEventsSubscription.unsubscribe();
        };
    }, [dispatch, requestFeaturedCustomList]);

    useEffect(() => {
        if (isFetchFinished(fetchStatus) && isLoadingConfig) {
            readyIndicator.setReady('hasData');
        }
    }, [fetchStatus, readyIndicator, isLoadingConfig]);

    return {
        config,
        matchesIds: memMatchesIds,
        fetchStatus,
        isSmall,
        isMedium,
        readyIndicator,
        isLoadingConfig,
    };
};

export default useFeaturedTournamentPage;
